import { VerticalAlignTopOutlined } from '@ant-design/icons';
import { Button, Checkbox, Switch, Table } from "antd";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import StickerFishHealth from '../../components/Location/FishHealth/StickerFishHealth';
import Lice from '../../components/Location/Lice';
import { openPopup2, POPUP_TYPE } from "../../contexts/PopupContext";
import { datetime } from "../../util/library/datetime";
import { selectOperation } from "../../util/store/operation/constants";
import { IUnitOverview, selectSite } from "../../util/store/site/constants";
import { siteActions } from "../../util/store/site/reducer";
import { diseaseType, FISH_HEALTH, fishHealth, productionArea, propContainer } from "../../util/varibles/constants";
import { checkZero } from '../../util/varibles/global';
import { IFishGroup } from '../../util/varibles/interface';
import { ColumnTemplates } from "../../util/varibles/template";
import { openSitePopup } from '../LocationPage/Sites/SiteDetail';
import { getAvgWeight, showAvgWeight } from '../PlanOperationPage/BiologyBrowser/_components/FishAndWeight';
import AvgWeight from './Filter/AvgWeight';
import SearchName from './Filter/SearchName';
import FishGroupChart from './FishGroupChart';
import { useSiteOverview } from "./SiteOverviewContext";
import styles from "./style.module.scss";

const ONLY_STARVING = "-";

export interface IFishgroupSelected {
    site_id: string
    site_name: string
    group_id: string
    group_name: string
}

const StarvingName = () => {
    const { data } = useSelector(selectOperation.starving);

    const onClick = () => {
        openPopup2(<div>
            {data.map((item, i) => <div className={styles.row} key={i}>
                {item.map((sub, j) => <div key={[i, j].join('_')}>
                    {sub}
                </div>)}
            </div>)}
        </div>, {
            modalProps: {
                width: 720,
                className: styles.starvingPopup,
                title: <div className='text-[14px] whitespace-break-spaces pr-[20px]'>
                    If fish must be starved for a set period before any Harvest or Treatment (see formula below). Ocean
                    Planner will display the Starving Date 7 days before the operation’s start.Ï
                </div>
            }
        })
    }

    return <div onClick={onClick}>
        Start starving date - day last
    </div>
}

const List: React.FC = () => {
    const { data: units, loading: unitLoading } = useSelector(selectSite.overview);
    const { data: fishGroup, loading: fishGroupLoading } = useSelector(selectSite.fishGroup);
    const [unitsfilter] = useSiteOverview(state => state.unitsFilter);
    const [fishgroupFilter] = useSiteOverview(state => state.fishgroupFilter);
    const dispatch = useDispatch();
    const siteTableRef: Parameters<typeof Table>[0]['ref'] = useRef(null);
    const fishGroupTableRef: Parameters<typeof Table>[0]['ref'] = useRef(null);
    const [isClosed, set] = useSiteOverview(state => state.isClosed);
    const [fishgroupSelected, setFishgroupSelected] = useState<IFishgroupSelected[]>([]);

    useEffect(() => {
        dispatch(siteActions.gets())
    }, [dispatch]);

    const handleUnitClick = useCallback((data: { id: string, name: string }) => {
        openSitePopup(data);
    }, []);

    const handleFishgroupClick = useCallback((data: IFishgroupSelected[]) => {
        openPopup2(<FishGroupChart source={data} />, {
            popupType: POPUP_TYPE.DRAWER,
            modalProps: {
                width: "100%",
                title: <div className='text-[20px] flex gap-1'>
                    <div className='font-[300]'>Biology overview:</div>
                    <div>Fish group ID chart</div>
                </div>
            }
        })
    }, []);

    const unitsFinal = useMemo(() => Object.keys(unitsfilter).length === 0 ? units : units.filter(item => {
        return Object.keys(unitsfilter).every(key => unitsfilter[key](item));
    }), [units, unitsfilter]);

    const fishGroupFinal = useMemo(() => Object.keys(fishgroupFilter).length === 0 ? fishGroup : fishGroup.filter(item => {
        return Object.keys(fishgroupFilter).every(key => !!item.close_date && fishgroupFilter[key](item));
    }), [fishGroup, fishgroupFilter]);

    const starvingDays = useMemo(() => {
        const opts = units.reduce((rs: Set<string>, item) => {
            const { starvings = [] } = item;
            starvings.forEach(sub => rs.add(datetime(sub.timestamp).format("MM/DD")))
            return rs;
        }, new Set([]));
        return [
            { value: ONLY_STARVING, text: "Starving units only" },
            ...Array.from(opts)
                .sort((a, b) => a.localeCompare(b))
                .map(key => {
                    const [m, d] = key.split("/");
                    const value = [d, m].join("/");
                    return { value, text: value }
                })
        ]
    }, [units]);

    return <div className={styles.list}>
        <div className='flex justify-between mb-10 gap-3'>
            <div className='flex gap-3 w-full'>
                <SearchName />
                {
                    !isClosed && <>
                        <AvgWeight />
                    </>
                }
            </div>
            <div className='flex items-center gap-3'>
                {fishgroupSelected.length > 0 && <div className='bt-default' onClick={() => handleFishgroupClick(fishgroupSelected)}>
                    Compare
                </div>}
                <div className='text-[14px] flex items-center gap-2'>
                    <div className='whitespace-nowrap'>Closed Fish Group ID </div>
                    <Switch value={isClosed} onChange={value => set({ isClosed: value })} />
                </div>
            </div>
        </div>
        <Table
            {...propContainer}
            ref={siteTableRef}
            className={styles.table}
            data-hide={isClosed}
            virtual
            scroll={{ y: window.innerHeight - 186 }}
            rowSelection={{
                columnWidth: 30,
                type: 'checkbox',
                onChange: (_: React.Key[], selectedRows: IUnitOverview[]) => {
                    setFishgroupSelected(prev => {
                        const result = selectedRows.reduce((rs: typeof prev, item) => [...rs, {
                            site_id: item.site.id,
                            site_name: item.site.name,
                            group_id: item.group_id || "",
                            group_name: item.group_name || ""
                        }], [])
                        return result
                    })
                },
                getCheckboxProps: (record: IFishGroup) => ({
                    disabled: (record.group_id || "").length === 0
                }),
            }}
            {...{
                rowKey: "id",
                pagination: false,
                columns: [
                    ColumnTemplates.Detail('Site name', data => data.site.name, data => handleUnitClick(data.site)),
                    { ...ColumnTemplates.Detail('Unit', data => data.unit_id, data => handleUnitClick(data.site)), width: 75 },
                    ColumnTemplates.Detail('Fish group Id', data => data.group_name, data => handleFishgroupClick([{ site_id: data.site.id, site_name: data.site.name, group_id: data.group_id, group_name: data.group_name }])),
                    {
                        ...ColumnTemplates.Number('Amount', "fish_amount"),
                        width: 86,
                    },
                    {
                        title: 'Avg. weight',
                        key: 'Avg. weight',
                        sorter: (a: IUnitOverview, b: IUnitOverview) => getAvgWeight(a) - getAvgWeight(b),
                        render: (data: IUnitOverview) => showAvgWeight(data),
                        width: 106
                    },
                    {
                        ...ColumnTemplates.NumberRender('Total weight', 'total_weight', data => Math.round(data.total_weight / 1000) / 1000, 'tn'),
                        width: 110
                    },
                    {
                        title: 'Lice info.',
                        key: 'lice',
                        width: 170,
                        sorter: (a: IUnitOverview, b: IUnitOverview) => (a.lice?.female_lice?.value || 0) - (b.lice?.female_lice?.value || 0),
                        render: (data: typeof units[0]) => <Lice data={data.lice} />
                    },
                    {
                        title: 'Fish health',
                        key: 'health',
                        dataIndex: 'fish_health',
                        width: 130,
                        render: (data: FISH_HEALTH) => {
                            const { name = '-' } = fishHealth[data || ''] || {};
                            return <div className='relative pl-4'>
                                <div className='absolute top-[3px] left-0'>
                                    <StickerFishHealth value={data} />
                                </div>
                                {name}
                            </div>
                        }
                    },
                    {
                        title: 'Diseases',
                        key: 'diseases',
                        render: (data: typeof units[0]) => {
                            const names: string[] = [];
                            if (data.site.diseases) {
                                const { cases = [] } = data.site.diseases[0] || {};
                                const { disease } = cases[0] || {};
                                if (disease) {
                                    const { name = '' } = diseaseType[disease] || {};
                                    names.push(name.toUpperCase());
                                }
                            }
                            if (data.unit_diseases)
                                data.unit_diseases.types.forEach(item => names.push(item.name));

                            return names.join(', ');
                        }
                    },
                    {
                        title: <StarvingName />,
                        key: 'starvingDate',
                        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
                            <div className='flex flex-col p-4 gap-2'>
                                {starvingDays.map(item => <Checkbox
                                    key={item.value}
                                    checked={new Set(selectedKeys).has(item.value)}
                                    onChange={({ target }) => {
                                        const { checked } = target;
                                        if (checked)
                                            if (item.value === ONLY_STARVING) {
                                                setSelectedKeys([ONLY_STARVING])
                                            } else {
                                                setSelectedKeys([...selectedKeys.filter(sub => sub !== ONLY_STARVING), item.value])
                                            }
                                        else {
                                            setSelectedKeys(selectedKeys.filter(sub => sub !== item.value))
                                        }
                                        confirm({ closeDropdown: false });
                                    }}
                                >
                                    {item.text}
                                </Checkbox>)}
                            </div>
                        ),
                        onFilter: (value, { starvings = [] }: typeof units[0]) => {
                            return value === ONLY_STARVING ? !!starvings[0] : starvings.some(item => value === datetime(item.timestamp).format("DD/MM"))
                        },
                        render: ({ starvings = [] }: typeof units[0]) => {
                            return starvings.map(item => `${datetime(item.timestamp).format('DD/MM')} (${item.starving_days}d)`).join(', ')
                        }
                    },
                    {
                        key: 'area',
                        width: 48,
                        render: ({ site }: typeof units[0]) => {
                            const value = site.prod_area ? productionArea[site.prod_area] : undefined;
                            return <div>
                                {value && `P${checkZero(value.id)}`}
                            </div>
                        }
                    }
                ],
                loading: unitLoading && !isClosed,
                dataSource: unitsFinal
            }} />
        <Table
            {...propContainer}
            ref={fishGroupTableRef}
            className={styles.table}
            data-hide={!isClosed}
            data-closed={true}
            virtual
            scroll={{ y: window.innerHeight - 186 }}
            rowSelection={{
                columnWidth: 30,
                type: 'checkbox',
                onChange: (_: React.Key[], selectedRows: IFishGroup[]) => {
                    setFishgroupSelected(prev => {
                        const result = selectedRows.reduce((rs: typeof prev, item) => [...rs, item], [])
                        return result
                    })
                    setFishgroupSelected(selectedRows)
                },
            }}
            {...{
                rowKey: "group_id",
                pagination: false,
                columns: [
                    {
                        ...ColumnTemplates.Detail('Site name', data => data.site_name, data => handleUnitClick({ id: data.site_id, name: data.site_name })),
                        width: 150
                    },
                    ColumnTemplates.Detail('Fish group Id', data => data.group_name, data => handleFishgroupClick([data])),
                    {
                        ...ColumnTemplates.Info('Orig. Fish group ID', "root_group"),
                        sorter: null
                    },
                    {
                        ...ColumnTemplates.Info('Created date', "open_date"),
                        width: 128,
                        sorter: (a: IFishGroup, b: IFishGroup) => datetime(a.open_date).time - datetime(b.open_date).time,
                    },
                    {
                        ...ColumnTemplates.Info('Harvested amount', "harvest_count"),
                        width: 148,
                        sorter: (a: IFishGroup, b: IFishGroup) => (+a.harvest_count || 0) - (+b.harvest_count || 0)
                    },
                    {
                        ...ColumnTemplates.Info('Harvested date', "close_date"),
                        width: 128,
                        sorter: (a: IFishGroup, b: IFishGroup) => datetime(a.close_date).time - datetime(b.close_date).time,
                    },
                    {
                        ...ColumnTemplates.Render('Accumulated mortality', data => ` ${typeof data.mortality_percent === "number" ? `${data.mortality_percent}%` : '-'}`),
                        width: 175,
                        sorter: (a: IFishGroup, b: IFishGroup) => (+a.mortality_percent || 0) - (+b.mortality_percent || 0),
                    },
                    {
                        ...ColumnTemplates.Info('Accumulate treatment', "treatment_count"),
                        width: 175,
                        sorter: (a: IFishGroup, b: IFishGroup) => (+a.mortality_percent || 0) - (+b.mortality_percent || 0),
                    },
                ],
                loading: fishGroupLoading && isClosed,
                dataSource: fishGroupFinal,
            }} />
        {(isClosed ? fishGroup.length > 0 : unitsFinal.length > 0) && <Button
            className={styles.scrollToTop}
            shape="circle"
            size="large"
            title='Scroll to top'
            icon={<VerticalAlignTopOutlined />}
            onClick={() => {
                siteTableRef.current?.scrollTo({ index: 0 })
            }}
        />}
    </div>;
};

export default List;
